import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { UserFormData } from "./api.service";
import { AgentType } from "../../../../interfaces";

// Interface for webhook data
export interface WebhookData {
  transcript?: Array<any>; // Use any to accommodate different transcript formats
  result?: string;
  [key: string]: any; // Allow for other webhook properties
}

@Injectable({
  providedIn: "root",
})
export class CallStateService {
  // BehaviorSubject to store the form data
  private formDataSubject = new BehaviorSubject<UserFormData | null>(null);

  // BehaviorSubject to store the agent name
  private agentNameSubject = new BehaviorSubject<AgentType | null>(null);

  // BehaviorSubject to store the invocation ID
  private invocationIdSubject = new BehaviorSubject<string>("");

  // BehaviorSubject to store the webhook data
  private webhookDataSubject = new BehaviorSubject<WebhookData | null>(null);

  // Observables that components can subscribe to
  public formData$: Observable<UserFormData | null> =
    this.formDataSubject.asObservable();

  public agentName$: Observable<AgentType | null> =
    this.agentNameSubject.asObservable();

  public invocationId$: Observable<string> =
    this.invocationIdSubject.asObservable();

  public webhookData$: Observable<WebhookData | null> =
    this.webhookDataSubject.asObservable();

  constructor() {}

  /**
   * Sets the form data to be shared between components
   * @param data The user form data
   */
  setFormData(data: UserFormData): void {
    this.formDataSubject.next(data);
  }

  /**
   * Sets the agent name to be shared between components
   * @param name The agent name (e.g., 'AYSA', 'WILA')
   */
  setAgentName(name: AgentType | null): void {
    this.agentNameSubject.next(name);
  }

  /**
   * Gets the current agent name
   * @returns The current agent name
   */
  getAgentName(): AgentType | null {
    return this.agentNameSubject.getValue();
  }

  getAgentNameDisplay(): string {
    return "SkySense AI™ Agent";
  }

  /**
   * Clears the stored form data
   */
  clearFormData(): void {
    this.formDataSubject.next(null);
  }

  /**
   * Sets the invocation ID to be shared between components
   * @param id The invocation ID from the API
   */
  setInvocationId(id: string): void {
    this.invocationIdSubject.next(id);
  }

  /**
   * Gets the current invocation ID
   * @returns The current invocation ID
   */
  getInvocationId(): string {
    return this.invocationIdSubject.getValue();
  }

  /**
   * Sets the webhook data
   * @param data The webhook data
   */
  setWebhookData(data: WebhookData): void {
    this.webhookDataSubject.next(data);
  }

  /**
   * Gets the current webhook data
   * @returns The current webhook data
   */
  getWebhookData(): WebhookData | null {
    return this.webhookDataSubject.getValue();
  }

  /**
   * Clears all stored data including form data, agent name, invocation ID, and webhook transcript
   */
  clearAllData(): void {
    this.formDataSubject.next(null);
    this.agentNameSubject.next(null);
    this.invocationIdSubject.next("");
    this.webhookDataSubject.next(null);
  }
}
